import { deriveGuidFrom, generate } from "library/guid/index.mjs"
import { buildTaskAmendsKey } from "authoring/routes/project/amends/lib/task-amends-key"
import { indexTasksInSchedule } from "routes/schedule/lib/index-tasks-in-schedule"
import { normalizeAuthoringPlainText } from "./authoring-comparison-normalization"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { extractMacroTokensFromString } from "authoring/routes/macro/lib/macro-token-utils"
import { isWithinFuzzyWordDistance } from "./fuzzy-text-similarity"

export const AUTHORING_RECOMMENDATION_STATE_READY = "ready"
export const AUTHORING_RECOMMENDATION_STATE_APPLIED = "applied"
export const AUTHORING_RECOMMENDATION_STATE_CONFLICTED = "conflicted"

export function resolveAuthoringRecommendationDraft({ target, rawSchedule } = {}) {
    return rawSchedule ?? target ?? null
}

export function getAuthoringRecommendationState({ recommendation, draft }) {
    if (!recommendation || !draft) return AUTHORING_RECOMMENDATION_STATE_CONFLICTED

    if (matchesRecommendationTarget({ recommendation, draft, mode: "applied" })) {
        return AUTHORING_RECOMMENDATION_STATE_APPLIED
    }

    if (matchesRecommendationTarget({ recommendation, draft, mode: "source" })) {
        return AUTHORING_RECOMMENDATION_STATE_READY
    }

    return AUTHORING_RECOMMENDATION_STATE_CONFLICTED
}

export function countAuthoringRecommendations({ recommendations = [], draft }) {
    return recommendations.reduce(
        (result, recommendation) => {
            const state = getAuthoringRecommendationState({ recommendation, draft })
            result.totalCount += 1
            if (state === AUTHORING_RECOMMENDATION_STATE_APPLIED) {
                result.appliedCount += 1
            } else {
                result.unappliedCount += 1
            }

            if (state === AUTHORING_RECOMMENDATION_STATE_READY) {
                result.readyCount += 1
            }

            if (state === AUTHORING_RECOMMENDATION_STATE_CONFLICTED) {
                result.conflictedCount += 1
            }

            return result
        },
        {
            totalCount: 0,
            unappliedCount: 0,
            appliedCount: 0,
            readyCount: 0,
            conflictedCount: 0,
        }
    )
}

export function filterVisibleAuthoringRecommendations({ recommendations = [], draft }) {
    return recommendations.filter(includeVisibleRecommendation)

    function includeVisibleRecommendation(recommendation) {
        if (recommendation?.sourceType !== "styleGuide") return true

        const state = getAuthoringRecommendationState({ recommendation, draft })
        return state !== AUTHORING_RECOMMENDATION_STATE_APPLIED
    }
}

export function canApplyAuthoringRecommendationState(state) {
    return state === AUTHORING_RECOMMENDATION_STATE_READY || state === AUTHORING_RECOMMENDATION_STATE_CONFLICTED
}

export function applyAuthoringRecommendationToDraft({ recommendation, draft, force = false }) {
    if (!recommendation || !draft) {
        return { applied: false, reason: "missing_recommendation_or_draft" }
    }

    const state = getAuthoringRecommendationState({ recommendation, draft })
    if (state === AUTHORING_RECOMMENDATION_STATE_APPLIED) {
        return {
            applied: true,
            amendsKey: resolveRecommendationAmendsKey({
                recommendation,
                task: findAuthoringRecommendationTask(draft, recommendation),
            }),
            state,
        }
    }
    if (!force && state !== AUTHORING_RECOMMENDATION_STATE_READY) {
        return { applied: false, reason: "conflicted", state }
    }
    if (force && !canApplyAuthoringRecommendationState(state)) {
        return { applied: false, reason: "conflicted", state }
    }

    draft.intro ??= {}
    draft.tasks ??= []

    const result = applyRecommendationChange({ recommendation, draft })
    if (!result?.applied) return result

    syncDraftTasks(draft)

    return {
        ...result,
        state: AUTHORING_RECOMMENDATION_STATE_READY,
        amendsKey: resolveRecommendationAmendsKey({ recommendation, task: result.task }),
    }
}

function applyRecommendationChange({ recommendation, draft }) {
    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
            draft.title = recommendation.newText || ""
            return { applied: true, task: null }
        case "intro_content":
            return applyIntroFieldChange({ recommendation, draft, field: "content" })
        case "intro_note":
            return applyIntroFieldChange({ recommendation, draft, field: "note" })
        case "task_content":
            return applyTaskFieldChange({ recommendation, draft, field: "content" })
        case "task_note":
            return applyTaskFieldChange({ recommendation, draft, field: "note" })
        case "task_title":
            return applyTaskFieldChange({ recommendation, draft, field: "title" })
        case "task_frequency":
            return applyTaskFrequencyChange({ recommendation, draft })
        case "remove_task":
            return removeTaskFromDraft({ recommendation, draft })
        case "add_task":
            return addTaskToDraft({ recommendation, draft })
        default:
            return { applied: false, reason: "unsupported_change_type" }
    }
}

function applyIntroFieldChange({ recommendation, draft, field }) {
    const currentValue = draft?.intro?.[field] || ""
    draft.intro[field] = preserveMacroOnlyIntroBlocks({
        currentValue,
        nextValue: recommendation.newText || "",
    })

    return { applied: true, task: null }
}

function applyTaskFieldChange({ recommendation, draft, field }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task) return { applied: false, reason: "task_not_found" }
    task[field] = recommendation.newText || ""
    return { applied: true, task }
}

function applyTaskFrequencyChange({ recommendation, draft }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task) return { applied: false, reason: "task_not_found" }
    const frequency = normalizeFrequency(recommendation.newFrequency)
    if (!frequency) return { applied: false, reason: "invalid_frequency" }
    task.frequency = frequency
    return { applied: true, task }
}

function removeTaskFromDraft({ recommendation, draft }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task) return { applied: false, reason: "task_not_found" }
    draft.tasks = draft.tasks.filter((candidate) => candidate !== task)
    return { applied: true, task }
}

function addTaskToDraft({ recommendation, draft }) {
    const task = normalizeAddedTask(recommendation.proposedTask)
    if (!task) return { applied: false, reason: "invalid_added_task" }
    draft.tasks.push(task)
    return { applied: true, task }
}

function matchesRecommendationTarget({ recommendation, draft, mode }) {
    const nextMode = mode === "applied" ? "applied" : "source"
    // "Applied" tolerates minor manual edits via a fuzzy word-distance check, anchored against the
    // source text so untouched content never reads as applied when old and new text are themselves
    // close. "Source" (ready) detection stays exact.
    const fuzzy = nextMode === "applied"
    const alternative = fuzzy ? selectModeText(recommendation, "source") : null

    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
            return comparePlainValue(draft?.title, selectModeText(recommendation, nextMode), fuzzy, alternative)
        case "intro_content":
            return compareIntroRichTextValue(
                draft?.intro?.content,
                selectModeText(recommendation, nextMode),
                fuzzy,
                alternative
            )
        case "intro_note":
            return compareIntroRichTextValue(
                draft?.intro?.note,
                selectModeText(recommendation, nextMode),
                fuzzy,
                alternative
            )
        case "task_content":
            return compareTaskField({ recommendation, draft, field: "content", mode: nextMode })
        case "task_note":
            return compareTaskField({ recommendation, draft, field: "note", mode: nextMode })
        case "task_title":
            return compareTaskField({ recommendation, draft, field: "title", mode: nextMode })
        case "task_frequency":
            return compareTaskFrequency({ recommendation, draft, mode: nextMode })
        case "remove_task":
            return compareRemovedTask({ recommendation, draft, mode: nextMode })
        case "add_task":
            return compareAddedTask({ recommendation, draft, mode: nextMode })
        default:
            return false
    }
}

function compareTaskField({ recommendation, draft, field, mode }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task) return false
    const fuzzy = mode === "applied"
    const alternative = fuzzy ? selectModeText(recommendation, "source") : null
    if (field === "content" || field === "note") {
        return compareRichTextValue(task?.[field], selectModeText(recommendation, mode), fuzzy, alternative)
    }
    if (field === "title") {
        return compareTaskTitleValue(task?.title, selectModeText(recommendation, mode), fuzzy, alternative)
    }
    return comparePlainValue(task?.[field], selectModeText(recommendation, mode), fuzzy, alternative)
}

function compareTaskFrequency({ recommendation, draft, mode }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (!task) return false

    if (mode === "applied") {
        return compareFrequencies(task?.frequency, recommendation?.newFrequency)
    }

    return normalizeFrequencyLabel(task?.frequency) === normalizeText(recommendation?.oldText)
}

function compareRemovedTask({ recommendation, draft, mode }) {
    const task = findAuthoringRecommendationTask(draft, recommendation)
    if (mode === "applied") return !task
    if (!task) return false
    return normalizeTaskRemovalText(task) === normalizeText(recommendation?.oldText)
}

function compareAddedTask({ recommendation, draft, mode }) {
    if (mode === "source") return !findMatchingAddedTask(draft?.tasks, recommendation?.proposedTask)
    return Boolean(findMatchingAddedTask(draft?.tasks, recommendation?.proposedTask, true))
}

export function findAuthoringRecommendationTask(draft, recommendation) {
    const tasks = Array.isArray(draft?.tasks) ? draft.tasks : []
    const resolvedTaskId = `${recommendation?.resolvedTaskId || ""}`.trim()
    const taskId = `${recommendation?.taskId || ""}`.trim()

    for (const candidate of tasks) {
        if (matchesTaskIdentifier(candidate, resolvedTaskId) || matchesTaskIdentifier(candidate, taskId)) {
            return candidate
        }
    }

    const taskNumber = Number(recommendation?.taskNumber)
    if (Number.isInteger(taskNumber) && taskNumber > 0) {
        return findTaskBySequence(tasks, taskNumber)
    }

    return null
}

function matchesTaskIdentifier(task, identifier) {
    if (!task || !identifier) return false
    const values = [task?.taskId, task?.temporaryTaskId, task?.id, task?.headerTaskId].filter(Boolean).map(String)
    return values.includes(String(identifier))
}

function findTaskBySequence(tasks, taskNumber) {
    let counter = 0

    for (const task of tasks) {
        if (task?.isHeader) continue
        counter += 1
        if (counter === taskNumber) return task
    }

    return null
}

function findMatchingAddedTask(tasks, proposedTask, fuzzy = false) {
    const normalizedTask = normalizeAddedTask(proposedTask)
    if (!normalizedTask) return null

    return (Array.isArray(tasks) ? tasks : []).find((task) => {
        if (task?.isHeader) return false
        return (
            comparePlainValue(task?.title, normalizedTask.title, fuzzy) &&
            comparePlainValue(task?.content, normalizedTask.content, fuzzy) &&
            comparePlainValue(task?.note, normalizedTask.note, fuzzy) &&
            `${task?.classification || "Amber"}` === `${normalizedTask.classification || "Amber"}` &&
            compareFrequencies(task?.frequency, normalizedTask.frequency)
        )
    })
}

function resolveRecommendationAmendsKey({ recommendation, task }) {
    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
            return "title"
        case "intro_content":
            return "intro.content"
        case "intro_note":
            return "intro.note"
        case "task_content":
        case "task_note":
        case "task_title":
        case "task_frequency":
        case "remove_task":
        case "add_task":
            return task ? buildTaskAmendsKey(task?.taskId || task?.temporaryTaskId || task?.id) : null
        default:
            return null
    }
}

function selectModeText(recommendation, mode) {
    return mode === "applied" ? recommendation?.newText : recommendation?.oldText
}

function comparePlainValue(left, right, fuzzy = false, alternative = null) {
    return compareNormalizedValues(
        normalizeText(stripChipHtml(left)),
        normalizeText(stripChipHtml(right)),
        fuzzy,
        alternative == null ? null : normalizeText(stripChipHtml(alternative))
    )
}

function compareTaskTitleValue(left, right, fuzzy = false, alternative = null) {
    return compareNormalizedValues(
        normalizeTaskTitleComparisonText(left),
        normalizeTaskTitleComparisonText(right),
        fuzzy,
        alternative == null ? null : normalizeTaskTitleComparisonText(alternative)
    )
}

function compareNormalizedValues(left, right, fuzzy, alternative = null) {
    if (left === right) return true
    if (!fuzzy) return false
    return isWithinFuzzyWordDistance(left, right, alternative)
}

function normalizeTaskTitleComparisonText(value) {
    const stripped = `${stripChipHtml(value) || ""}`.trim()
    if (!stripped) return ""
    if (looksLikeTaskTitleHtml(stripped)) {
        return normalizeAuthoringPlainText(stripped)
    }
    return normalizePlainTitleComparisonText(stripped)
}

function normalizePlainTitleComparisonText(value) {
    return `${value || ""}`.replace(/\s+/g, " ").trim().toLowerCase()
}

function looksLikeTaskTitleHtml(value) {
    return /<[^>]+>/.test(`${value || ""}`)
}

function compareRichTextValue(left, right, fuzzy = false, alternative = null) {
    return compareNormalizedValues(
        normalizeAuthoringPlainText(stripChipHtml(left)),
        normalizeAuthoringPlainText(stripChipHtml(right)),
        fuzzy,
        alternative == null ? null : normalizeAuthoringPlainText(stripChipHtml(alternative))
    )
}

function compareIntroRichTextValue(left, right, fuzzy = false, alternative = null) {
    return compareNormalizedValues(
        normalizeIntroComparisonText(left),
        normalizeIntroComparisonText(right),
        fuzzy,
        alternative == null ? null : normalizeIntroComparisonText(alternative)
    )
}

function normalizeText(value) {
    return typeof value === "string" ? value.trim() : `${value || ""}`.trim()
}

function normalizeIntroComparisonText(value) {
    return normalizeAuthoringPlainText(stripMacroOnlyBlocks(value))
}

function preserveMacroOnlyIntroBlocks({ currentValue, nextValue }) {
    const normalizedCurrent = normalizeRichTextBlocks(currentValue)
    const normalizedNext = normalizeRichTextBlocks(nextValue)
    const currentBlocks = getTopLevelBlocks(normalizedCurrent)
    if (!currentBlocks.some((block) => isMacroOnlyBlock(block))) {
        return normalizedNext
    }

    const nextContentBlocks = getTopLevelBlocks(normalizedNext).filter((block) => !isMacroOnlyBlock(block))
    if (nextContentBlocks.length === 0) return normalizedCurrent

    const mergedBlocks = []
    let nextIndex = 0

    for (const currentBlock of currentBlocks) {
        if (isMacroOnlyBlock(currentBlock)) {
            mergedBlocks.push(currentBlock)
            continue
        }

        if (nextIndex < nextContentBlocks.length) {
            mergedBlocks.push(nextContentBlocks[nextIndex])
            nextIndex += 1
        }
    }

    while (nextIndex < nextContentBlocks.length) {
        mergedBlocks.push(nextContentBlocks[nextIndex])
        nextIndex += 1
    }

    return mergedBlocks.join("")
}

function stripMacroOnlyBlocks(value) {
    return getTopLevelBlocks(normalizeRichTextBlocks(value))
        .filter((block) => !isMacroOnlyBlock(block))
        .join("")
}

function isMacroOnlyBlock(blockHtml) {
    const tokenizedBlock = stripChipHtml(blockHtml)
    const tokens = Array.from(extractMacroTokensFromString(tokenizedBlock))
    if (tokens.length === 0) return false

    const withoutTokens = tokens.reduce((result, tokenId) => result.replaceAll(`$${tokenId}$`, " "), tokenizedBlock)
    const withoutMarkup = `${withoutTokens || ""}`
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, " ")
        .trim()

    return withoutMarkup.length === 0
}

function getTopLevelBlocks(value) {
    const normalizedValue = normalizeRichTextBlocks(value)
    if (!normalizedValue) return []

    const container = document.createElement("div")
    container.innerHTML = normalizedValue

    return Array.from(container.childNodes).map(serializeNode).filter(Boolean)
}

function serializeNode(node) {
    if (!node) return ""
    if (node.nodeType === Node.ELEMENT_NODE) return node.outerHTML
    if (node.nodeType !== Node.TEXT_NODE) return ""

    const text = `${node.textContent || ""}`.trim()
    if (!text) return ""
    const paragraph = document.createElement("p")
    paragraph.textContent = text
    return paragraph.outerHTML
}

function normalizeRichTextBlocks(value) {
    const html = `${value || ""}`.trim()
    if (!html) return ""
    if (/^\s*<(p|div|ol|ul|table|blockquote|h[1-6])\b/i.test(html)) return html

    const paragraph = document.createElement("p")
    paragraph.innerHTML = html
    return paragraph.outerHTML
}

function normalizeFrequencyLabel(frequency) {
    const normalized = normalizeFrequency(frequency)
    if (!normalized) return ""
    return `${normalized.interval} ${normalized.period}`.trim()
}

function compareFrequencies(left, right) {
    const normalizedLeft = normalizeFrequency(left)
    const normalizedRight = normalizeFrequency(right)
    if (!normalizedLeft || !normalizedRight) return false
    return (
        Number(normalizedLeft.interval) === Number(normalizedRight.interval) &&
        `${normalizedLeft.period || ""}` === `${normalizedRight.period || ""}`
    )
}

function normalizeFrequency(value) {
    if (!value || typeof value !== "object") return null
    const interval = Number(value.interval)
    const period = `${value.period || ""}`.trim()
    if (!Number.isFinite(interval) || interval <= 0 || !period) return null
    return {
        interval: Math.round(interval),
        period,
    }
}

function normalizeTaskRemovalText(task) {
    return normalizeText([task?.title, task?.content, task?.note].filter(Boolean).join("\n\n"))
}

function normalizeAddedTask(raw) {
    if (!raw || typeof raw !== "object") return null

    return {
        id: raw.id ?? generateTaskGuid(),
        taskId: raw.taskId ?? generateTaskGuid(),
        title: `${raw.title || raw.taskTitle || "New task"}`,
        content: `${raw.content || ""}`,
        note: `${raw.note || ""}`,
        classification: `${raw.classification || "Amber"}`,
        skillSet: raw.skillSet != null ? `${raw.skillSet}` : "1",
        minutes: Number.isFinite(Number(raw.minutes)) ? Number(raw.minutes) : 0,
        unitOfMeasure: `${raw.unitOfMeasure || "Nr"}`,
        isHeader: Boolean(raw.isHeader),
        frequency: normalizeFrequency(raw.frequency) || { interval: 1, period: "Month" },
    }
}

function generateTaskGuid() {
    return deriveGuidFrom(generate())
}

function syncDraftTasks(draft) {
    const scheduleId = `${draft?.id || ""}`.trim()
    let headerIndex = 0
    let taskIndex = 0

    for (let index = 0; index < (draft?.tasks?.length || 0); index += 1) {
        const task = draft.tasks[index]
        if (!task) continue
        task.index = index
        task.id = `${scheduleId}.${task?.isHeader ? `HH${headerIndex++}` : `${taskIndex++}`}`
    }

    indexTasksInSchedule(draft)
}
